/**
 * Episode Sidebar Module
 * Handles metadata sidebar rendering and notes
 */

import { API } from '../core/api.js';
import { showNotification } from '../components/notifications.js';
import { escapeHtml, stripHtml } from '../core/utils.js';
import { renderChatInterface, setupChatHandlers, loadPersistedChat } from './episode-chat.js';
import { getUniqueSpeakers, setupSpeakerNameInputs } from './episode-transcription.js';

/**
 * Render metadata sidebar with tabs
 */
export function renderMetadataSidebar(episode) {
    const container = document.getElementById('episode-metadata');
    if (!container) return;

    container.textContent = '';

    // Create tabs
    const tabsDiv = document.createElement('div');
    tabsDiv.className = 'sidebar-tabs';

    const metadataTab = document.createElement('button');
    metadataTab.className = 'sidebar-tab active';
    metadataTab.dataset.tab = 'metadata';
    metadataTab.textContent = 'Info';
    metadataTab.onclick = () => switchSidebarTab('metadata');

    const chatTab = document.createElement('button');
    chatTab.className = 'sidebar-tab';
    chatTab.dataset.tab = 'chat';
    chatTab.textContent = 'Chat';
    chatTab.onclick = () => switchSidebarTab('chat');

    tabsDiv.appendChild(metadataTab);
    tabsDiv.appendChild(chatTab);
    container.appendChild(tabsDiv);

    // Create metadata content
    const metadataContent = document.createElement('div');
    metadataContent.className = 'sidebar-tab-content';
    metadataContent.id = 'tab-metadata';
    metadataContent.innerHTML = renderMetadataContent(episode);
    container.appendChild(metadataContent);

    // Create chat content
    const chatContent = document.createElement('div');
    chatContent.className = 'sidebar-tab-content';
    chatContent.id = 'tab-chat';
    chatContent.style.display = 'none';
    chatContent.innerHTML = renderChatInterface(episode);
    container.appendChild(chatContent);

    // Setup handlers
    setupSpeakerNameInputs(episode);
    setupNotesInput(episode);
    setupChatHandlers(episode);
    loadPersistedChat(episode);
}

/**
 * Switch between sidebar tabs
 */
export function switchSidebarTab(tabName) {
    document.querySelectorAll('.sidebar-tab').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.tab === tabName);
    });

    document.querySelectorAll('.sidebar-tab-content').forEach(content => {
        content.style.display = content.id === `tab-${tabName}` ? 'block' : 'none';
    });
}

/**
 * Render metadata content
 */
function renderMetadataContent(episode) {
    const sections = [];

    // AI Analysis section
    const llmStatus = episode.llm_status || 'pending';
    const statusText = llmStatus === 'completed' ? 'Analyzed' :
                       llmStatus === 'processing' ? 'Processing...' : 'Not analyzed';
    const statusClass = llmStatus === 'completed' ? 'status-completed' :
                        llmStatus === 'processing' ? 'status-processing' : 'status-pending';
    sections.push(`
        <div class="metadata-section ai-analysis-section">
            <h3>AI Analysis</h3>
            <div class="ai-status-row">
                <span class="ai-status ${statusClass}">${statusText}</span>
                <button class="btn btn-small ${llmStatus === 'processing' ? 'btn-disabled' : 'btn-primary'}"
                        onclick="analyzeEpisode(${episode.id})"
                        ${llmStatus === 'processing' ? 'disabled' : ''}>
                    ${llmStatus === 'completed' ? 'Re-analyze' : 'Analyze'}
                </button>
            </div>
        </div>
    `);

    // Reading preferences
    const savedFont = localStorage.getItem('transcript-font') || 'sans';
    sections.push(`
        <div class="metadata-section reading-prefs-section">
            <h3>Reading</h3>
            <div class="reading-pref-row">
                <label for="font-selector">Font</label>
                <select id="font-selector" class="font-selector" onchange="changeTranscriptFont(this.value)">
                    <option value="sans" ${savedFont === 'sans' ? 'selected' : ''}>Sans-serif</option>
                    <option value="serif" ${savedFont === 'serif' ? 'selected' : ''}>Serif</option>
                    <option value="mono" ${savedFont === 'mono' ? 'selected' : ''}>Mono</option>
                </select>
            </div>
        </div>
    `);

    // Speaker mappings
    const speakers = getUniqueSpeakers(episode);
    if (speakers.length > 0) {
        const speakerNames = episode.speaker_names || {};
        sections.push(`
            <div class="metadata-section speaker-mapping-section">
                <h3>Speakers</h3>
                ${speakers.map((speaker, index) => `
                    <div class="speaker-mapping-row">
                        <span class="speaker-color-dot" data-speaker-index="${index % 8}"></span>
                        <span class="speaker-label-original">${escapeHtml(speaker)}</span>
                        <input type="text"
                               class="speaker-name-input"
                               data-speaker="${escapeHtml(speaker)}"
                               value="${escapeHtml(speakerNames[speaker] || '')}"
                               placeholder="Enter name..."
                        />
                    </div>
                `).join('')}
            </div>
        `);
    }

    // Description
    if (episode.description) {
        sections.push(`
            <div class="metadata-section collapsible">
                <h3 class="collapsible-header" onclick="toggleSection(this)">
                    Description
                    <span class="collapse-icon">▼</span>
                </h3>
                <div class="collapsible-content">
                    <div class="episode-description">${escapeHtml(stripHtml(episode.description))}</div>
                </div>
            </div>
        `);
    }

    // Notes
    sections.push(`
        <div class="metadata-section notes-section">
            <h3>Notes</h3>
            <textarea id="episode-notes"
                      class="episode-notes-input"
                      placeholder="Add your notes about this episode..."
                      data-episode-id="${episode.id}"></textarea>
            <div class="notes-save-status" id="notes-save-status"></div>
        </div>
    `);

    // Entities
    if (episode.entities && Object.keys(episode.entities).length > 0) {
        const entitiesHtml = Object.entries(episode.entities)
            .map(([type, items]) => {
                if (!items || items.length === 0) return '';
                return `
                    <div class="entity-group">
                        <span class="entity-type">${escapeHtml(type)}:</span>
                        <span class="entity-items">${items.map(i => escapeHtml(i)).join(', ')}</span>
                    </div>
                `;
            })
            .filter(Boolean)
            .join('');

        if (entitiesHtml) {
            sections.push(`
                <div class="metadata-section">
                    <h3>Entities</h3>
                    ${entitiesHtml}
                </div>
            `);
        }
    }

    // Keywords
    if (episode.keywords && episode.keywords.length > 0) {
        sections.push(`
            <div class="metadata-section">
                <h3>Keywords</h3>
                <div class="keyword-tags">
                    ${episode.keywords.map(k => `<span class="keyword-tag">${escapeHtml(k)}</span>`).join('')}
                </div>
            </div>
        `);
    }

    // Concepts
    if (episode.concepts && episode.concepts.length > 0) {
        sections.push(`
            <div class="metadata-section">
                <h3>Concepts</h3>
                <ul class="concept-list">
                    ${episode.concepts.map(c => `<li>${escapeHtml(c)}</li>`).join('')}
                </ul>
            </div>
        `);
    }

    // Books mentioned
    if (episode.books_mentioned && episode.books_mentioned.length > 0) {
        sections.push(`
            <div class="metadata-section">
                <h3>Books Mentioned</h3>
                <ul class="books-list">
                    ${episode.books_mentioned.map(b => `<li>${escapeHtml(b)}</li>`).join('')}
                </ul>
            </div>
        `);
    }

    // Summary
    if (episode.summary) {
        sections.push(`
            <div class="metadata-section">
                <h3>Summary</h3>
                <p class="episode-summary">${escapeHtml(episode.summary)}</p>
            </div>
        `);
    }

    // Links mentioned
    if (episode.links_mentioned && episode.links_mentioned.length > 0) {
        sections.push(`
            <div class="metadata-section">
                <h3>Links Mentioned</h3>
                <ul class="links-list">
                    ${episode.links_mentioned.map(link => `
                        <li><a href="${escapeHtml(link)}" target="_blank" rel="noopener">${escapeHtml(link)}</a></li>
                    `).join('')}
                </ul>
            </div>
        `);
    }

    // Ideas to explore
    if (episode.ideas_to_explore && episode.ideas_to_explore.length > 0) {
        sections.push(`
            <div class="metadata-section">
                <h3>Ideas to Explore</h3>
                <ul class="ideas-list">
                    ${episode.ideas_to_explore.map(idea => `<li>${escapeHtml(idea)}</li>`).join('')}
                </ul>
            </div>
        `);
    }

    return sections.length > 0
        ? sections.join('')
        : '<p class="no-metadata">No metadata available for this episode.</p>';
}

/**
 * Toggle collapsible section
 */
export function toggleSection(headerElement) {
    const section = headerElement.closest('.collapsible');
    if (section) {
        section.classList.toggle('collapsed');
    }
}

/**
 * Setup notes textarea with debounced auto-save
 */
function setupNotesInput(episode) {
    const textarea = document.getElementById('episode-notes');
    if (!textarea) return;

    textarea.value = episode.notes || '';

    let saveTimeout = null;
    const statusEl = document.getElementById('notes-save-status');

    textarea.addEventListener('input', () => {
        if (statusEl) {
            statusEl.textContent = 'Saving...';
            statusEl.classList.add('visible');
        }

        clearTimeout(saveTimeout);
        saveTimeout = setTimeout(() => saveNotes(episode, textarea.value), 1000);
    });
}

/**
 * Save notes to server
 */
async function saveNotes(episode, notes) {
    const statusEl = document.getElementById('notes-save-status');

    try {
        await API.put(`/api/episodes/${episode.id}/notes`, { notes });

        episode.notes = notes;
        if (window.currentEpisodeData) {
            window.currentEpisodeData.notes = notes;
        }

        if (statusEl) {
            statusEl.textContent = 'Saved';
            setTimeout(() => statusEl.classList.remove('visible'), 2000);
        }
    } catch (error) {
        console.error('Error saving notes:', error);
        if (statusEl) {
            statusEl.textContent = 'Failed to save';
            statusEl.style.color = 'var(--error)';
        }
        showNotification('Failed to save notes', 'error');
    }
}
